import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { FormHelperText, MenuItem, Stack, TextField } from '@mui/material';

// ---------------------------------------------------------------------------
// Selector de fecha de nacimiento en tres dropdowns (Día / Mes / Año) — el
// mismo patrón del flujo gratis del nahual, mucho más cómodo en móvil que
// escribir "dd/mm/aaaa" o pelearse con el <input type="date"> nativo.
//
// Mantiene su propio estado de los tres selects (para poder armar la fecha de
// a uno) y emite hacia el padre un string ISO 'YYYY-MM-DD' — o '' mientras la
// fecha esté incompleta o sea inválida (p.ej. 31 de febrero), para que la
// validación de cada pantalla la bloquee. Como el valor entra/sale en ISO, no
// cambia validación ni backend.
//
// Es la única fuente del selector: lo usan los flujos gratis (numerología,
// carta astral, sinastría, nahual) y el wrapper de react-hook-form RHFBirthDate
// para las páginas de pago. Es agnóstico de estilo: cada pantalla puede pasar
// su propio TextField estilizado vía `StyledField` y/o `fieldSx`.

const MONTHS_ES = [
  { value: 1, label: 'Enero' },
  { value: 2, label: 'Febrero' },
  { value: 3, label: 'Marzo' },
  { value: 4, label: 'Abril' },
  { value: 5, label: 'Mayo' },
  { value: 6, label: 'Junio' },
  { value: 7, label: 'Julio' },
  { value: 8, label: 'Agosto' },
  { value: 9, label: 'Septiembre' },
  { value: 10, label: 'Octubre' },
  { value: 11, label: 'Noviembre' },
  { value: 12, label: 'Diciembre' },
];

const CURRENT_YEAR = new Date().getFullYear();

function daysInMonth(month, year) {
  if (!month) return 31;
  // Date(year, month, 0) devuelve el último día del mes anterior (1-indexed).
  return new Date(year || 2000, month, 0).getDate();
}

const pad = (n) => String(n).padStart(2, '0');

// Parsear 'YYYY-MM-DD' → {day, month, year} (números) o vacíos.
function parseISO(value) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || '');
  if (!m) return { day: '', month: '', year: '' };
  return { year: Number(m[1]), month: Number(m[2]), day: Number(m[3]) };
}

BirthDateSelect.propTypes = {
  value: PropTypes.string, // 'YYYY-MM-DD' o '' (solo para prefill inicial)
  onChange: PropTypes.func.isRequired, // recibe el string ISO (o '' si incompleto)
  onBlur: PropTypes.func,
  error: PropTypes.bool,
  helperText: PropTypes.node,
  disabled: PropTypes.bool,
  variant: PropTypes.string,
  fieldSx: PropTypes.object,
  spacing: PropTypes.number,
  StyledField: PropTypes.elementType, // TextField estilizado opcional
  futureYears: PropTypes.number, // años hacia el futuro (default 0 = solo pasado)
};

export default function BirthDateSelect({
  value,
  onChange,
  onBlur,
  error = false,
  helperText,
  disabled = false,
  variant,
  fieldSx,
  spacing = 1.5,
  StyledField,
  futureYears = 0,
}) {
  // Rango de años válido: por defecto hoy → 100 años atrás (nacimientos). Con
  // `futureYears` se incluyen N años hacia adelante (p.ej. fecha de boda).
  const minYear = CURRENT_YEAR - 100;
  const maxYear = CURRENT_YEAR + futureYears;
  // Estado interno (inicializado del value para prefill). Es la fuente de
  // verdad: así se puede llenar día/mes/año de a uno sin que la selección
  // parcial se borre. El año se teclea (4 dígitos): en sesiones reales el
  // dropdown de ~100 años costaba 40+ segundos y mis-taps al público 55+.
  const initial = parseISO(value);
  const [day, setDay] = useState(initial.day);
  const [month, setMonth] = useState(initial.month);
  const [yearText, setYearText] = useState(initial.year ? String(initial.year) : '');

  const yearNum = yearText.length === 4 ? Number(yearText) : 0;
  const yearOutOfRange = yearText.length === 4 && (yearNum < minYear || yearNum > maxYear);
  const year = yearOutOfRange ? 0 : yearNum;

  // Emitir el ISO al padre cuando cambian los selects. Usamos un ref para el
  // callback para no meterlo en las deps (evita re-emitir/loop si el padre pasa
  // un onChange recreado en cada render, como el inline del nahual).
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  useEffect(() => {
    if (!day || !month || !year || day > daysInMonth(month, year)) {
      onChangeRef.current('');
      return;
    }
    onChangeRef.current(`${year}-${pad(month)}-${pad(day)}`);
  }, [day, month, year]);

  const maxDay = daysInMonth(month, year);
  const dayOptions = Array.from({ length: maxDay }, (_, i) => i + 1);

  const Field = StyledField || TextField;
  const commonProps = { select: true, disabled, error, onBlur };
  if (variant) commonProps.variant = variant;

  return (
    <div>
      <Stack direction="row" spacing={spacing}>
        <Field
          {...commonProps}
          label="Día"
          value={day || ''}
          onChange={(e) => setDay(Number(e.target.value))}
          sx={{ flex: '0 0 28%', ...fieldSx }}
        >
          {dayOptions.map((d) => (
            <MenuItem key={d} value={d}>{d}</MenuItem>
          ))}
        </Field>

        <Field
          {...commonProps}
          label="Mes"
          value={month || ''}
          onChange={(e) => setMonth(Number(e.target.value))}
          sx={{ flex: '1 1 auto', ...fieldSx }}
        >
          {MONTHS_ES.map((m) => (
            <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
          ))}
        </Field>

        <Field
          {...commonProps}
          select={false}
          label="Año"
          placeholder="1970"
          value={yearText}
          error={error || yearOutOfRange}
          onChange={(e) => setYearText(e.target.value.replace(/\D/g, '').slice(0, 4))}
          inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 4 }}
          sx={{ flex: '0 0 34%', ...fieldSx }}
        />
      </Stack>
      {yearOutOfRange && (
        <FormHelperText error sx={{ mx: '14px' }}>
          Revisa el año ({minYear}–{maxYear})
        </FormHelperText>
      )}
      {!!helperText && (
        <FormHelperText error={error} sx={{ mx: '14px' }}>{helperText}</FormHelperText>
      )}
    </div>
  );
}

// Utilidades de conversión reutilizables para pantallas que manejan Date.
export function dateToISO(date) {
  if (!date || Number.isNaN(date.getTime())) return '';
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export function isoToDate(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || '');
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
}
